<section class="step compose" aria-labelledby="sb-compose-title">
  <div class="compose__grid">
    <!-- Sender, name and the message -->
    <div class="bk-panel panel compose__form">
      <header class="panel__head">
        <div class="panel__titles">
          <h2 class="bk-section-title panel__title" id="sb-compose-title" tabindex="-1">Write the message</h2>
          <p class="panel__sub">
            Write it once. Each field is swapped for that row’s own value before it goes out.
          </p>
        </div>
      </header>

      <div class="compose__pair">
        <div class="bk-field">
          <label class="bk-field__label" for="sb-sender">Sender ID</label>
          <bk-select
            inputId="sb-sender"
            describedBy="sb-sender-hint"
            [options]="senderIDs"
            valueKey="senderId"
            variant="sender"
            noun="sender ID"
            placeholder="Choose a sender ID"
            emptyText="No sender IDs available"
            [loading]="senderLoading"
            [value]="store.senderIdSig()"
            (valueChange)="store.senderId = $event"
          />
          @if (senderFailed) {
            <p class="bk-field__hint" id="sb-sender-hint">
              Sender IDs didn’t load. Go back a step and return here to try again.
            </p>
          } @else if (!senderLoading && !senderIDs.length) {
            <p class="bk-field__hint" id="sb-sender-hint">
              No sender IDs are set up for your organisation yet. Ask an admin to add one.
            </p>
          } @else {
            <p class="bk-field__hint" id="sb-sender-hint">The name people see the message come from.</p>
          }
        </div>

        <div class="bk-field" [class.is-invalid]="nameTouched && !store.subjectSig().trim()">
          <label class="bk-field__label" for="sb-name">Broadcast name</label>
          <input
            id="sb-name"
            class="bk-field__input"
            maxlength="120"
            autocomplete="off"
            placeholder="e.g. October fees reminder"
            [attr.aria-invalid]="nameTouched && !store.subjectSig().trim() ? 'true' : null"
            [attr.aria-describedby]="nameTouched && !store.subjectSig().trim() ? 'sb-name-error' : 'sb-name-hint'"
            [value]="store.subjectSig()"
            (input)="store.messageSubject = asValue($event)"
            (blur)="nameTouched = true"
          />
          @if (nameTouched && !store.subjectSig().trim()) {
            <p class="bk-field__error" id="sb-name-error">
              <span class="bk-sym" aria-hidden="true">error</span>
              Name the broadcast so you can find its messages later.
            </p>
          } @else {
            <p class="bk-field__hint" id="sb-name-hint">
              Every message carries this name in Messages. Pick one you’ll recognise.
            </p>
          }
        </div>
      </div>

      <div class="bk-field">
        <label class="bk-field__label" for="sb-body">Message</label>
        <textarea
          #bodyArea
          id="sb-body"
          class="bk-field__input compose__body"
          rows="7"
          placeholder="Type your message, then insert fields from the list below."
          [value]="store.bodySig()"
          (input)="store.messageBody = asValue($event)"
        ></textarea>
      </div>

      <!-- Insert a field at the cursor -->
      <div class="insert">
        <p class="insert__label" id="sb-insert-label">Insert a field</p>
        <div class="insert__list" role="group" aria-labelledby="sb-insert-label">
          @for (column of visibleFields; track column.index) {
            <button
              type="button"
              class="insert__btn"
              [class.is-used]="usedSet().has(column.field)"
              [attr.aria-label]="insertLabel(column.field)"
              (click)="insertField(column.field)"
            >
              <span class="bk-sym" aria-hidden="true">add</span>
              {{ column.field }}
            </button>
          }
          @if (fieldList().length > fieldPreview) {
            <button
              type="button"
              class="bk-more insert__more"
              [attr.aria-expanded]="showAllFields"
              (click)="showAllFields = !showAllFields"
            >
              {{ showAllFields ? "Show fewer" : "Show all " + fieldList().length }}
            </button>
          }
        </div>
      </div>

      <!-- Fields already in the message, removable -->
      @if (store.usedFields().known.length) {
        <div class="used">
          <p class="insert__label">In this message</p>
          <ul #usedList class="used__list">
            @for (field of store.usedFields().known; track field; let i = $index) {
              <li class="bk-chip used__chip">
                <span class="used__name">{{ token(field) }}</span>
                <button
                  type="button"
                  class="used__remove"
                  [attr.aria-label]="'Remove field ' + field"
                  [matTooltip]="'Remove from the message'"
                  (click)="removeAttribute(field, i)"
                >
                  <span class="bk-sym" aria-hidden="true">close</span>
                </button>
              </li>
            }
          </ul>
        </div>
      }

      @if (notes().length) {
        <ul class="notes">
          @for (note of notes(); track $index) {
            <li class="note" [attr.data-tone]="note.tone">
              <span>
                {{ note.text }}
                @if (note.more) {
                  <span class="note__more">{{ note.more }}</span>
                }
              </span>
            </li>
          }
        </ul>
      }

      <!-- Empty values: what to show instead of {{field}} -->
      @if (emptyFields().length) {
        <div class="fallbacks">
          <div class="fallbacks__head">
            <h3 class="bk-eyebrow">Empty values</h3>
            <p class="fallbacks__sub">
              Some rows have no value for a field. Without a fallback, those people see the field as written,
              like {{ token(emptyFields()[0].field) }}.
            </p>
          </div>
          @for (item of emptyFields(); track item.field; let i = $index) {
            <div class="fallback">
              <label class="fallback__label" [for]="'sb-fallback-' + i">
                <span class="token">{{ token(item.field) }}</span>
                is empty in {{ formatCount(item.count) }} {{ item.count === 1 ? "row" : "rows" }}
              </label>
              <input
                class="bk-field__input"
                autocomplete="off"
                placeholder="Use instead, e.g. Customer"
                [id]="'sb-fallback-' + i"
                [value]="store.fallbacks()[item.field] || ''"
                (input)="setFallback(item.field, $event)"
              />
              <p class="fallback__state">
                @if (store.fallbacks()[item.field]) {
                  Those rows get “{{ store.fallbacks()[item.field] }}”.
                } @else {
                  Those rows show {{ token(item.field) }} as written.
                }
              </p>
            </div>
          }
        </div>
      }
    </div>

    <!-- What each row gets -->
    <div class="bk-panel panel compose__preview">
      <sb-row-preview heading="What each person gets" [testable]="true" />
      @if (store.totals().messages) {
        <p class="compose__totals" [class.is-settling]="store.settling()">
          {{ formatCount(store.totals().messages) }}
          {{ store.totals().messages === 1 ? "message" : "messages" }},
          {{ formatCount(store.totals().parts) }} SMS {{ store.totals().parts === 1 ? "part" : "parts" }} in all
          @if (store.totals().maxParts > 1) {
            <span class="compose__sep" aria-hidden="true">·</span>
            the longest uses {{ store.totals().maxParts }}
          }
        </p>
      }
    </div>
  </div>

  <footer class="step__foot">
    <button type="button" class="bk-btn bk-btn--ghost" (click)="back.emit()">
      <span class="bk-sym" aria-hidden="true">arrow_back</span>
      Back
    </button>
    <div class="step__foot-end">
      @if (!canContinue) {
        <span class="step__why" id="sb-compose-why">{{ missing }}</span>
      }
      <button
        type="button"
        class="bk-btn bk-btn--primary"
        [attr.aria-disabled]="!canContinue || null"
        [attr.aria-describedby]="canContinue ? null : 'sb-compose-why'"
        (click)="continue()"
      >
        Review
        <span class="bk-sym trail" aria-hidden="true">arrow_forward</span>
      </button>
    </div>
  </footer>
</section>
